/**
 * Utility functions for ParamOverridesEditor component.
 * Extracted for testability.
 */

import type {
  ParamOverride,
  ParameterDependency,
  ParameterSchema,
  ParameterSchemaBoolean,
  ParameterSchemaCategorical,
  ParameterSchemaNumeric,
} from "@tradingdroid/frontends-types";
import intersection from "lodash-es/intersection";

import { getOverrideMode } from "./types";

/**
 * Type guard for categorical parameters.
 */
export const isCategoricalSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaCategorical => {
  return schema?.type === "CategoricalParameter";
};

/**
 * Type guard for boolean parameters.
 */
export const isBooleanSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaBoolean => {
  return schema?.type === "BooleanParameter";
};

/**
 * Type guard for numeric parameters (int or float).
 */
export const isNumericSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaNumeric => {
  return schema?.type === "IntParameter" || schema?.type === "FloatParameter";
};

/**
 * Check if a parameter should be visible based on its dependencies.
 * A param is visible if ALL its dependencies are satisfied.
 *
 * When the target parameter is optimizing over multiple values (either via explicit
 * categories override OR default optimize=true), the dependency is satisfied if
 * ANY of those values matches the condition.
 */
export const isParamVisible = (
  dependencies: ParameterDependency[] | undefined,
  currentValues: Record<string, unknown>,
  parameterSchema?: Record<string, ParameterSchema>,
  overrides?: Record<string, ParamOverride>,
): boolean => {
  if (!dependencies || dependencies.length === 0) {
    return true;
  }

  for (const dep of dependencies) {
    const targetOverride = overrides?.[dep.target];
    const targetSchema = parameterSchema?.[dep.target];

    // Determine which values the target is optimizing over
    let targetCategories: (string | boolean)[] | null = null;

    if (targetOverride && "categories" in targetOverride && targetOverride.categories.length > 0) {
      // Case 1: Explicit categories override
      targetCategories = targetOverride.categories;
    } else if (
      !targetOverride &&
      targetSchema &&
      "categories" in targetSchema &&
      targetSchema.optimize !== false
    ) {
      // Case 2: No override, but target is categorical with optimize=true (default)
      // This means all target categories are selected ("Values (D)" mode)
      targetCategories = targetSchema.categories;
    }

    if (targetCategories && targetCategories.length > 0) {
      // Multi-value mode: check if ANY of the target values satisfies the condition
      const conditionSatisfied = targetCategories.some((val) => val === dep.condition);
      if (!conditionSatisfied) {
        return false;
      }
    } else {
      // Single value mode: check against currentValue
      const targetValue = currentValues[dep.target];
      if (targetValue !== dep.condition) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Get the effective categories for a categorical parameter,
 * applying conditionalCategories restrictions based on parent value(s).
 *
 * When parent is optimizing over multiple values (either via explicit categories override,
 * or by default when parent.optimize=true and no override is set),
 * returns the UNION of all effective categories for each selected parent value.
 *
 * NOTE: Only applies to CategoricalParameter, NOT BooleanParameter.
 * BooleanParameter always has categories [true, false] and no conditionalCategories.
 */
export const getEffectiveCategories = (
  schema: ParameterSchemaCategorical,
  currentValues: Record<string, unknown>,
  parameterSchema: Record<string, ParameterSchema>,
  overrides?: Record<string, ParamOverride>,
): string[] => {
  if (!schema.conditionalCategories || !schema.conditionalParent) {
    return schema.categories;
  }

  const parentSchema = parameterSchema[schema.conditionalParent];
  const parentOverride = overrides?.[schema.conditionalParent];

  // Determine which parent categories are being optimized over:
  // 1. If parent has explicit categories override → use those
  // 2. If parent has NO override AND parent is categorical with optimize=true → use ALL parent's categories
  // 3. Otherwise → single value mode
  let parentCategoriesToUse: (string | boolean)[] | null = null;

  if (parentOverride && "categories" in parentOverride && parentOverride.categories.length > 0) {
    // Case 1: Explicit categories override
    parentCategoriesToUse = parentOverride.categories;
  } else if (
    !parentOverride &&
    parentSchema &&
    "categories" in parentSchema &&
    parentSchema.optimize !== false
  ) {
    // Case 2: No override, but parent is categorical with optimize=true (default)
    // This means all parent categories are selected by default ("Values (D)" mode)
    parentCategoriesToUse = parentSchema.categories;
  }

  if (parentCategoriesToUse && parentCategoriesToUse.length > 0) {
    // Compute union of effective categories for all selected parent values
    const unionSet = new Set<string>();
    for (const parentVal of parentCategoriesToUse) {
      const conditional = schema.conditionalCategories[parentVal as string];
      if (conditional) {
        for (const cat of conditional) {
          unionSet.add(cat);
        }
      }
    }
    // If we found categories from conditionalCategories, return the union
    if (unionSet.size > 0) {
      return Array.from(unionSet);
    }
    // Fallback to all categories if no conditional matches
    return schema.categories;
  }

  // Single value mode: use current value or fallback to parent's default
  let parentValue = currentValues[schema.conditionalParent];
  if (parentValue === undefined) {
    parentValue = parentSchema?.default;
  }

  // Apply conditional categories if parent value maps to a restriction
  if (parentValue !== undefined && schema.conditionalCategories[parentValue as string]) {
    const conditional = schema.conditionalCategories[parentValue as string];
    // Fallback to all categories if conditional filtering results in empty array
    return conditional.length > 0 ? conditional : schema.categories;
  }

  return schema.categories;
};

/**
 * Get current values from overrides + strategy defaults + schema defaults.
 * Uses multi-pass approach to handle categories overrides correctly.
 */
export const getCurrentValues = (
  parameterSchema: Record<string, ParameterSchema>,
  strategyDefaults: Record<string, unknown>,
  overrides: Record<string, ParamOverride>,
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};

  // First pass: resolve values for params without categories override
  for (const [name, schema] of Object.entries(parameterSchema)) {
    const override = overrides[name];

    // A null fixed value is an emptied box, not a value. Treating it as one would resolve this
    // parameter to null while the job it produces omits the parameter and runs on the strategy's
    // default -- so a dependent parameter could be shown or hidden on a value the run never uses.
    if (override && "fixed" in override && override.fixed != null) {
      values[name] = override.fixed;
    } else if (!override || !("categories" in override)) {
      if (strategyDefaults[name] !== undefined) {
        values[name] = strategyDefaults[name];
      } else {
        values[name] = schema.default;
      }
    }
  }

  // Second pass: resolve values for params WITH categories override
  for (const [name, schema] of Object.entries(parameterSchema)) {
    const override = overrides[name];

    if (override && "categories" in override && override.categories.length > 0) {
      if (isCategoricalSchema(schema)) {
        const effective = getEffectiveCategories(schema, values, parameterSchema, overrides);
        const validSelection = (override.categories as string[]).filter((c) =>
          effective.includes(c),
        );

        if (validSelection.length > 0) {
          values[name] = validSelection.includes(schema.default)
            ? schema.default
            : validSelection[0];
        } else if (effective.length > 0) {
          values[name] = effective.includes(schema.default) ? schema.default : effective[0];
        } else {
          // Fallback to schema default if effective categories is empty
          values[name] = schema.default;
        }
      } else {
        values[name] = override.categories[0];
      }
    }
  }

  // Third pass: ensure categorical params without categories override have valid values
  for (const [name, schema] of Object.entries(parameterSchema)) {
    if (values[name] === undefined && isCategoricalSchema(schema)) {
      const effective = getEffectiveCategories(schema, values, parameterSchema, overrides);
      values[name] = effective[0];
    }
  }

  // Include strategy defaults for params not in schema
  for (const [name, value] of Object.entries(strategyDefaults)) {
    if (!(name in parameterSchema) && !(name in values)) {
      values[name] = value;
    }
  }

  return values;
};

// Parameters hidden from optimization UI (no effect in backtests)
const HIDDEN_PARAMS = new Set(["stop_loss_timing", "take_profit_timing"]);

/**
 * Build the FULL overrides payload from all displayed parameter values.
 *
 * This follows the "what you see is what you get" principle:
 * - Every visible parameter generates an override entry
 * - No "dirty" tracking - we send everything as displayed
 * - Backend receives exactly what the user sees in the UI
 *
 * For each param, builds the appropriate override based on the current displayed mode:
 * - fixed: { fixed: displayedValue }
 * - range: { min, max, step? }
 * - categories: { categories: [...] }
 */
export const buildFullOverridesPayload = (
  parameterSchema: Record<string, ParameterSchema>,
  strategyDefaults: Record<string, unknown>,
  overrides: Record<string, ParamOverride>,
): Record<string, ParamOverride> => {
  const result: Record<string, ParamOverride> = {};
  const currentValues = getCurrentValues(parameterSchema, strategyDefaults, overrides);

  for (const [name, schema] of Object.entries(parameterSchema)) {
    // Skip hidden params
    if (HIDDEN_PARAMS.has(name)) continue;

    // Skip params that aren't visible due to dependencies
    if (!isParamVisible(schema.dependencies, currentValues, parameterSchema, overrides)) {
      continue;
    }

    const existingOverride = overrides[name];
    const defaultValue = strategyDefaults[name];

    // The mode the editor displays for this parameter is the mode the study receives. One
    // resolver for both, so an untouched Range or Values parameter is never sent as fixed.
    const currentMode = getOverrideMode(existingOverride, schema, defaultValue);

    // Build the override based on current mode
    if (currentMode === "fixed") {
      // Fixed mode: send the displayed value
      const displayedValue =
        existingOverride && "fixed" in existingOverride
          ? existingOverride.fixed
          : (defaultValue ?? schema.default);
      // A parameter with no default shows an empty box, and an empty box is not a value. Posting
      // { fixed: null } would assert one the user never typed, and it does not even survive: the
      // worker type-checks a numeric fixed override and raises on null, so the job fails outright.
      // Leaving the parameter out is what "unset" means, and the strategy's own default stands.
      if (displayedValue != null) {
        result[name] = { fixed: displayedValue };
      }
    } else if (currentMode === "range" && isNumericSchema(schema)) {
      // Range mode: send min/max/step as displayed
      if (existingOverride && "min" in existingOverride && "max" in existingOverride) {
        result[name] = { ...existingOverride };
      } else {
        // No override - use schema defaults
        const rangeOverride: ParamOverride = {
          min: schema.low,
          max: schema.high,
        };
        if (schema.step !== undefined) {
          rangeOverride.step = schema.step;
        }
        result[name] = rangeOverride;
      }
    } else if (currentMode === "categories") {
      // Categories mode: send the displayed categories
      if (existingOverride && "categories" in existingOverride) {
        result[name] = { categories: [...existingOverride.categories] };
      } else {
        // No override - use all effective categories (default behavior)
        if (isBooleanSchema(schema)) {
          result[name] = { categories: [...schema.categories] };
        } else if (isCategoricalSchema(schema)) {
          const effectiveCats = getEffectiveCategories(
            schema,
            currentValues,
            parameterSchema,
            overrides,
          );
          result[name] = { categories: [...effectiveCats] };
        }
      }
    }
  }

  // Also include default-only params (params in strategy defaults but not in schema)
  for (const [name, value] of Object.entries(strategyDefaults)) {
    if (!(name in parameterSchema) && !HIDDEN_PARAMS.has(name)) {
      const existingOverride = overrides[name];
      if (existingOverride && "fixed" in existingOverride) {
        // An emptied box is not a value, so the parameter is left out and the strategy's own
        // default stands. Note this deliberately does not fall through to the default below:
        // posting that would assert the very value the user just cleared away.
        if (existingOverride.fixed != null) {
          result[name] = { fixed: existingOverride.fixed };
        }
      } else if (value != null) {
        // Same rule as the schema loop above: an absent value is not a value, so it is left out
        // rather than posted as null. Today's only caller derives these defaults from the schema
        // itself, so nothing reaches here, but this helper is exported and a defaults map from
        // anywhere else -- a stored job config, say -- can carry an explicit null.
        result[name] = { fixed: value };
      }
    }
  }

  return result;
};

/**
 * A searched categorical whose selected values leave nothing for one value its parent can take.
 */
export interface EmptyConditionalBranch {
  param: string;
  parent: string;
  parentValue: string;
  selected: string[];
  allowed: string[];
}

// The categories a payload entry searches, or null when the entry does not search this parameter.
// The worker drops a value the parameter does not declare and keeps the first copy of a repeat.
// A parameter the payload leaves out reads as its declared search: its categories, or null
// when it is declared fixed.
const searchedCategories = (
  schema: ParameterSchemaCategorical,
  override: ParamOverride | undefined,
): string[] | null => {
  if (!override) {
    return schema.optimize !== false ? schema.categories : null;
  }
  if (!("categories" in override)) {
    return null;
  }
  return intersection(override.categories.map(String), schema.categories);
};

// The values a parent can take in the study: the categories it searches, or its one fixed value.
const reachableParentValues = (
  schema: ParameterSchemaCategorical,
  override: ParamOverride | undefined,
): string[] => {
  if (override && "fixed" in override && override.fixed != null) {
    return [String(override.fixed)];
  }
  return searchedCategories(schema, override) ?? [schema.default];
};

/**
 * Find the searched categoricals in an overrides payload that keep no value for a parent value the
 * study can reach.
 *
 * For a parent value the study can reach, the worker searches that value's conditional list
 * filtered by the parameter's own categories, and it refuses the optimization at start when that
 * list is empty.
 * When the parent searches several values, the editor offers the union of their conditional lists,
 * so it can build that state: a calc type without `indicator` while its stop type still searches
 * `indicator`.
 * It reads only the parent named by `conditionalParent`, and skips a parent whose schema type is
 * not `CategoricalParameter`.
 */
export const findEmptyConditionalBranches = (
  parameterSchema: Record<string, ParameterSchema>,
  payload: Record<string, ParamOverride>,
): EmptyConditionalBranch[] => {
  const branches: EmptyConditionalBranch[] = [];

  for (const [name, schema] of Object.entries(parameterSchema)) {
    if (
      !isCategoricalSchema(schema) ||
      !schema.conditionalCategories ||
      !schema.conditionalParent
    ) {
      continue;
    }
    const parentSchema = parameterSchema[schema.conditionalParent];
    if (!isCategoricalSchema(parentSchema)) {
      continue;
    }
    const selected = searchedCategories(schema, payload[name]);
    if (!selected || selected.length === 0) {
      continue;
    }

    for (const parentValue of reachableParentValues(
      parentSchema,
      payload[schema.conditionalParent],
    )) {
      const allowed = schema.conditionalCategories[parentValue];
      if (allowed && !allowed.some((cat) => selected.includes(cat))) {
        branches.push({
          param: name,
          parent: schema.conditionalParent,
          parentValue,
          selected,
          allowed,
        });
      }
    }
  }

  return branches;
};

/**
 * One line for an empty branch: the parameter's searched values, the parent value, and what
 * that value allows.
 */
export const describeEmptyConditionalBranch = (branch: EmptyConditionalBranch): string =>
  `${branch.param}: no selected value (${branch.selected.join(", ")}) is allowed when ` +
  `${branch.parent} is ${branch.parentValue}, which allows ${branch.allowed.join(", ")}.`;
